// Castintech dashboard — bearer token auth helper (Faz 30).
// Cookie session ile login yapilmis kullanici bearer-only endpoint'lere
// (admin-bearer requireAdminBearer middleware'i ile korunan POST'lar)
// istek attiginda 403 doner. Bu modul:
//   - authBearerFetch(): Cookie + opsiyonel Bearer header birlikte attach eder.
//   - getAdminBearer(): localStorage'dan token okur (https/localhost'ta aktif).
//   - setAdminBearer(): kullanicidan prompt ile alip localStorage'a yazar.
//   - clearAdminBearer(): logout/test sirasinda silmek icin.
//
// Guvenlik notu (XSS):
//   - Token sadece `localhost`, `127.0.0.1` veya `https:` origin'de saklanir.
//   - HTTP origin'de getAdminBearer() null doner; setAdminBearer() reddeder.
//   - localStorage XSS'e karsi savunmasiz — kabul edilebilir cunku kullanici
//     kendi makinesi + dashboard cookie zaten ayni risk seviyesinde.
//
// Wire pattern:
//   import { authBearerFetch, ensureAdminBearer } from './auth.js';
//   const ok = await ensureAdminBearer(); if (!ok) return;  // prompt
//   const r = await authBearerFetch('/castintech-api/hotfix/approve', {
//     method: 'POST', body: JSON.stringify({...}),
//   });
//   if (r.status === 403) { clearAdminBearer(); ... }

const STORAGE_KEY = 'castintech_admin_bearer';

// Sadece secure origin'de bearer storage aktif. Localhost dev + production
// HTTPS desteklenir; HTTP production'da token gizli kalir.
export function isStorageAllowed(): boolean {
  if (typeof window === 'undefined') return false;
  const proto = window.location.protocol;
  const host = window.location.hostname;
  if (proto === 'https:') return true;
  if (host === 'localhost' || host === '127.0.0.1' || host === '::1') return true;
  return false;
}

export function getAdminBearer(): string | null {
  if (!isStorageAllowed()) return null;
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

export function setAdminBearer(token: string): boolean {
  if (!isStorageAllowed()) {
    console.warn('auth: bearer storage devre disi (insecure origin)');
    return false;
  }
  try {
    localStorage.setItem(STORAGE_KEY, token);
    return true;
  } catch {
    return false;
  }
}

export function clearAdminBearer(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch { /* ignore */ }
}

// Kullaniciya prompt acar (window.prompt — basit ve XSS-safe). Token yoksa veya
// kullanici iptal ederse false doner; token saklanirsa true.
export function ensureAdminBearer(reason?: string): boolean {
  let tok = getAdminBearer();
  if (tok) return true;
  if (!isStorageAllowed()) {
    alert('Admin token sadece HTTPS veya localhost\'ta saklanabilir.');
    return false;
  }
  const msg = reason
    ? `Admin token gerekli (${reason}). CASTINTECH_ADMIN_KEY degerini girin:`
    : 'Admin token gerekli. CASTINTECH_ADMIN_KEY degerini girin:';
  const input = window.prompt(msg, '');
  if (!input || !input.trim()) return false;
  tok = input.trim();
  return setAdminBearer(tok);
}

// fetch wrapper: cookie credentials + opsiyonel Bearer header.
export function authBearerFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const tok = getAdminBearer();
  const headers = new Headers(init.headers || {});
  if (!headers.has('Content-Type') && init.body) {
    headers.set('Content-Type', 'application/json');
  }
  if (tok) {
    headers.set('Authorization', 'Bearer ' + tok);
  }
  return fetch(url, {
    ...init,
    credentials: 'same-origin',
    headers,
  });
}

// 403 dondu mu kontrol eder, donduyse token'i temizler ve kullaniciya yeniden
// prompt acar. Caller true alirsa fetch'i tekrarlayabilir.
export async function handle403AndRetry(reason?: string): Promise<boolean> {
  clearAdminBearer();
  return ensureAdminBearer(reason);
}
